import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { Session } from "@wharfkit/session";
import { login as walletLogin, logout as walletLogout, restore } from "./session";
import { logConnection } from "../api/connectionLog";

interface WalletContextValue {
  session: Session | undefined;
  account: string | undefined;
  connecting: boolean;
  connect: () => Promise<void>;
  disconnect: () => Promise<void>;
}

const WalletContext = createContext<WalletContextValue | undefined>(undefined);

export function WalletProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | undefined>(undefined);
  const [connecting, setConnecting] = useState(false);

  // Try to silently resume a previous session on first load.
  useEffect(() => {
    restore()
      .then((s) => {
        if (s) {
          setSession(s);
          const acct = s.actor?.toString();
          if (acct) logConnection(acct, "restored");
        }
      })
      .catch(() => {
        /* no previous session to restore, that's fine */
      });
  }, []);

  async function connect() {
    setConnecting(true);
    try {
      const s = await walletLogin();
      setSession(s);
      const acct = s.actor?.toString();
      if (acct) logConnection(acct, "explicit");
    } finally {
      setConnecting(false);
    }
  }

  async function disconnect() {
    await walletLogout(session);
    setSession(undefined);
  }

  const value: WalletContextValue = {
    session,
    account: session?.actor?.toString(),
    connecting,
    connect,
    disconnect,
  };

  return <WalletContext.Provider value={value}>{children}</WalletContext.Provider>;
}

export function useWallet(): WalletContextValue {
  const ctx = useContext(WalletContext);
  if (!ctx) throw new Error("useWallet must be used within a WalletProvider");
  return ctx;
}
